// hooks/categories/useCategories.ts
import { useState, useEffect, useCallback } from "react";
import { Categorie } from "../../../../types/features/vitrine/Post";
import { categorieService } from "../../../../services/vitrine/categorieService";


const PRODUCT_NAME = 'izicallcenter';

export default function useCategories() {
  const [categories, setCategories] = useState<Categorie[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Récupérer toutes les catégories
  const fetchCategories = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await categorieService.getAll(page, perPage);
      setCategories(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des catégories");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Récupérer une catégorie par ID
  const fetchCategoryById = useCallback(async (id: string): Promise<Categorie | null> => {
    setLoading(true);
    setError(null);
    try {
      const category = await categorieService.getById(id);
      return category;
    } catch (err) {
      setError("Erreur lors du chargement de la catégorie");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ NOUVEAU - Récupérer les catégories par produit
  const fetchCategoriesByProduct = useCallback(async (
    productName: string = PRODUCT_NAME,
    page: number = 1,
    perPage: number = 20
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await categorieService.getByProductName(productName, page, perPage);
      setCategories(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des catégories par produit");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ NOUVEAU - Récupérer les catégories par produit avec posts
  const fetchCategoriesByProductWithPosts = useCallback(async (
    productName: string = PRODUCT_NAME,
    page: number = 1,
    perPage: number = 20
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await categorieService.getByProductNameWithPosts(productName, page, perPage);
      setCategories(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des catégories avec posts");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategoriesByProduct();
  }, []);

  return {
    categories,
    loading,
    error,
    pagination,
    fetchCategories,
    fetchCategoryById,
    fetchCategoriesByProduct,
    fetchCategoriesByProductWithPosts,
  };
}